import {
  identify as fullstoryIdentify,
  init as fullstoryInit,
  isInitialized as isFullstoryInitialized,
} from '@fullstory/browser'
import { paths } from '@/constants/paths'
import { isProductionEnvironment } from '@/utils/environment-utils'
import { getBooleanFromSessionStorage, writeToSessionStorage } from '@/utils/session-storage'
import { getIsAngelEmployeeUser } from '@/utils/users'
import { namedLogger } from '../utils'
import { type AnalyticsPlugin } from './types'

/**
 * The sampling rate should correspond with our plan with FullStory. See https://app.fullstory.com/ui/o-1ESZ78-na1/settings/billing
 * We are currently on the free plan, which allows for 10,000 sessions per month.
 * The quota resets on the 1st of each month.
 * In the future, we may want to add additional logic to only track sessions on specific pages at a given sampling rate.
 * Might make a lot of sense to move this config to Contentful.
 * We'd probably want something like: [ { pathRegex: RegExp, sampleRate: number } ]
 */
const FULL_STORY_SAMPLE_RATE = 0.01

type AnalyticsEventParams<Payload extends object = object> = {
  payload: Payload
}

const logger = namedLogger('fullstory Analytics Plugin', {
  // debug: true,
})

export function fullstoryAnalyticsPlugin() {
  const plugin: AnalyticsPlugin = {
    name: 'fullstory',

    page({ payload }: { payload?: { properties?: { path?: string } } }) {
      logger.debug('page', { payload })
      if (typeof window === 'undefined') return
      logger.debug('path', payload?.properties?.path)
      if (!payload?.properties?.path) return
      const shouldTrack = shouldTrackFullStoryImpression(payload.properties.path)
      logger.debug('shouldTrack', { shouldTrack })
      if (!shouldTrack) return

      logger.debug('initializing')

      fullstoryInit({
        orgId: process.env.NEXT_PUBLIC_FULL_STORY_ORG_ID || '',
        devMode: !isProductionEnvironment(),
      })
    },

    identify: ({
      payload,
    }: AnalyticsEventParams<{
      userId?: string
      anonymousId?: string
      traits?: {
        email?: string
        displayName?: string
        hasVPPAConsent?: boolean
      }
    }>) => {
      const { userId, traits } = payload
      if (!userId) return

      const isAngelEmployeeUser = getIsAngelEmployeeUser(traits?.email)
      if (isAngelEmployeeUser) return

      if (!isFullstoryInitialized()) return

      const custom = traits?.hasVPPAConsent ? { displayName: traits.displayName, email: traits.email || '' } : undefined

      fullstoryIdentify(userId, custom)
    },

    loaded() {
      logger.debug('loaded')
      return true
    },
  }

  return plugin
}

function shouldTrackFullStoryImpression(path: string): boolean {
  if (path !== `${paths.guild.join}/angel-studios` && path !== paths.guild.join) return false

  // Check if we've already made a sampling decision for this session
  const hasStoredDecision = getBooleanFromSessionStorage('fullStoryTracked')
  // if we have a stored decision, return it
  if (typeof hasStoredDecision === 'boolean') {
    return hasStoredDecision
  }

  // Make sampling decision and store it
  const shouldTrack = Math.random() < FULL_STORY_SAMPLE_RATE
  writeToSessionStorage('fullStoryTracked', shouldTrack)
  return shouldTrack
}
